// PWA install banner — ports service-worker.js showInstallPrompt / install().
// Shows on the chat screen when the browser has offered installation
// (beforeinstallprompt captured in main.jsx), the device is mobile and not yet
// installed, the user hasn't snoozed it, and the post-registration/payment
// install guide hasn't already shown this session. Appears after a short grace
// delay (lets Android deliver deferredPrompt). "Установить" triggers the native
// prompt; ✕ or an outside click snoozes it for a day (installStore).
//
// Placement (InstallPrompt.module.css): phones keep the centered bottom-sheet;
// tablets pin it bottom-left within the sidebar width so it never covers the
// chat composer.

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useInstallStore } from '../store/installStore'
import { useInstallGuideStore } from '../store/installGuideStore'
import { useNotifyPromptStore } from '../store/notifyPromptStore'
import { isMobileDevice, isStandalonePwa } from '../lib/install'
import { isNative } from '../lib/platform'
import styles from './InstallPrompt.module.css'

// Grace period before the banner appears: gives Android time to fire
// beforeinstallprompt and softens the entrance (not slapped onto first paint).
const READY_DELAY_MS = 4000

export default function InstallPrompt() {
  const deferredPrompt = useInstallStore((s) => s.deferredPrompt)
  const dismissed = useInstallStore((s) => s.dismissed)
  const dismiss = useInstallStore((s) => s.dismiss)
  const clear = useInstallStore((s) => s.clear)
  const guideShown = useInstallGuideStore((s) => s.shownThisSession)
  const guidePending = useInstallGuideStore((s) => s.pending)
  const notifyOpen = useNotifyPromptStore((s) => s.open)
  const { pathname } = useLocation()
  const [ready, setReady] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setReady(true), READY_DELAY_MS)
    return () => clearTimeout(id)
  }, [])

  const eligible =
    Boolean(deferredPrompt) &&
    !dismissed &&
    ready &&
    pathname === '/' &&
    // Stay out of the way of the post-registration overlays: the guide already
    // shown this session, the guide queued behind the notification prompt, or
    // the notification prompt itself open — otherwise the banner stacks on top.
    !guideShown &&
    !guidePending &&
    !notifyOpen &&
    isMobileDevice() &&
    !isStandalonePwa()

  // Outside-click snoozes (req 5). No visual backdrop so the chat stays usable.
  useEffect(() => {
    if (!eligible) return
    const onOutside = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) dismiss()
    }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('touchstart', onOutside)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
    }
  }, [eligible, dismiss])

  // Native (Capacitor): `beforeinstallprompt` never fires in the WebView and the
  // app is already installed — never show the web install banner. (isStandalonePwa()
  // also returns true on native, so this is explicit rather than load-bearing.)
  if (isNative || !eligible) return null

  const install = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    await deferredPrompt.userChoice.catch(() => {})
    clear()
  }

  return (
    <div className={styles.sheet} ref={ref}>
      <button className={styles.dismissBtn} onClick={dismiss} aria-label="Закрыть">✕</button>
      <div className={styles.header}>
        <img src="/icon-192.png" alt="" className={styles.icon} />
        <div className={styles.content}>
          <h3>Установить Humanteq</h3>
          <p>Добавьте приложение на&nbsp;главный экран для быстрого доступа</p>
        </div>
      </div>
      <button className={styles.installBtn} onClick={install}>Установить</button>
    </div>
  )
}
